/**
 * Email-Automatisierungen v6.1 — Prozess-Hierarchie.
 *
 * Vier Segmente auf /email-automation (Query-Param `seg`). Der Drill in L2/L3
 * laeuft ueber ECHTE Routen (nicht mehr Query-Params) → Browser-Back arbeitet
 * exakt rueckwaerts:
 *
 *   /email-automation                       L1 (Segment via ?seg, Filter ?dept/?lever)
 *   /email-automation/p/:prozessId          L2 Prozess
 *   /email-automation/p/:prozessId/t/:docTyp L3 Task
 *   /email-automation/hebel/:leverId        L2 Hebel
 *   /email-automation/roadmap/stufe/:n      L2 Roadmap-Stufe
 *   /email-automation/roadmap/cluster/:id   L3 Roadmap-Cluster
 *
 * Segment + Filter bleiben Query-Params (Filter-Wechsel mit replace:true → kein
 * History-Muell). Alle Detailseiten teilen EIN Entity-Template (Kopfband +
 * KPI-Chips + Aktionsleiste + einheitliche Body-Sektionen). Deutsche Labels via
 * DOC_TYP_LABEL / ACT_LABEL / PATH_LABEL / CAP_LABEL. Datenquelle unveraendert:
 * GET /email-automation/decision-board + GET /email-automation. Board bleibt
 * read-only; DecisionBlock/StatusEditor-Mechanik wird wiederverwendet.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  Search,
  Star,
} from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { Card, EmptyState, Chip, KpiCard, ValueBar, SectionHeader, DataTable, Thead, Th, Trow } from "../ui";
import { useToast } from "../ui/toast";
import { DecisionDrawer, type DrawerCard } from "./EmailAutomationDecisionDrawer";
import {
  useDecisions,
  useCardStatuses,
  DecisionBlock,
  fmtInt,
  STATUS_LABEL,
  STATUS_STEPS,
  type CardStatus,
  type Decision,
  type CardStatusRow,
} from "./EmailAutomationDecide";
import {
  ExampleCard,
  AttachmentPreview,
  sfThreadUrl,
  type Attachment,
  type Coverage,
  type Example,
  type UseCaseDetail,
} from "./EmailAutomation";
import { EntityPage, KpiChip, BackButton, docLabel, akteurLabel, keyActivate } from "./emailAutomationEntity";

// ═════════════════════════════════════════════════════════════════════════════
// Typen — Board-JSON v3
// ═════════════════════════════════════════════════════════════════════════════
interface DeptAnteile {
  hours_per_year: Record<string, number>;
  total_hours_per_year: number;
  share: Record<string, number>;
}
type Ampel = "gruen" | "gelb" | "rot";
interface SollSchritt {
  schritt: string; processing_path?: string; doc_types?: string[];
  akteur_von_an?: string; baustein_refs?: string[]; luecke?: string; ampel?: Ampel;
}
interface KartenRoi { saved_hours_6mo: number | null; hours_per_year: number | null; roi_quelle?: string }
interface ProzessTask {
  doc_typ: string; volumen_6m: number; volumen_quelle: string; sf_bestand?: string;
  verarbeitungspfad?: string; verarbeitungspfad_begruendung?: string;
  zustaendige_karten?: string[]; karten_roi?: Record<string, KartenRoi>;
}
interface ProzessFamilie {
  family_id: string; label: string; set: string; threads: number;
  saved_hours_6mo: number; hours_per_year: number; triage: string;
  triage_begruendung?: string; soll_prozess?: SollSchritt[]; tasks?: ProzessTask[];
  dept_anteile?: DeptAnteile;
}
interface Prozesskette {
  chain_id: string; name: string; beschreibung: string; hours_per_year: number;
  saved_hours_6mo: number; prozess_count: number; dept_anteile?: DeptAnteile;
  familien: ProzessFamilie[];
}
interface QuickWinV3 {
  id: string; titel: string; plattform: string; pattern?: string; triage?: string;
  status: string; effort_gesamt: string; hebel?: string; hebel_quelle?: string;
  doc_typ?: string; familien?: string[]; prerequisites?: string[];
  saved_hours_6mo?: number; hours_per_year?: number; roi_quelle?: string;
  materialized_from?: string; volumen?: Record<string, unknown>;
  entscheidungen_offen?: { frage: string; optionen?: string[] }[];
  bestand?: string; luecke?: string; risiko?: string;
  automation_uc_ids?: string[];   // Karte → Automatisierung(en) (P1.4-Mapping)
}
interface PrereqV3 {
  id: string; platform: string; titel: string; beschreibung_kurz: string;
  effort: string; blocking: string; status: string;
  unlocked_capability?: string | null;
  unlock_hours_per_year?: number; unlock_hours_6mo?: number;
  unlock_cluster_count?: number; unlock_mapping_note?: string;
}
interface Stufe {
  stufe: number; name: string; status: string; cluster: number; hours: number;
  by_dept?: Record<string, number>;
}
interface Cluster {
  id: string; name: string; name_de?: string; dept: string; freq?: number; saved_hours_6mo?: number;
  stage: number; required_capabilities: string[]; rationale?: string; rationale_de?: string;
  recipe_sketch?: {
    trigger?: string; steps?: string[]; end_states?: string[];
    trigger_de?: string; steps_de?: string[]; endstates_de?: string[];
  } | null;
  recipe_provenienz?: string | null; warum_autonom?: string; warum_autonom_de?: string;
  urteil_bleibt_human?: string; urteil_bleibt_human_de?: string;
  heutiger_ablauf?: string; reassign_suggestion?: string; reassign_suggestion_de?: string;
  begruendung_ausfuehrlich?: string;
  stage1_scope?: string; stage1_scope_de?: string;
  teil2_recipe_vorziehbar?: string; teil2_recipe_vorziehbar_de?: string;
}
interface Felder {
  outbound_data_fields: string[]; inbound_extract_fields: string[];
  inbound_checks: string[]; enrichment_gaps: string[];
}
interface AttRef {
  uc_id: string; example_index: number; filename?: string | null;
  internet_message_id?: string; mailbox?: string; thread_id?: string; subject?: string;
}
interface DigiSpec {
  karte: string; titel: string; doc_types: string[]; felder: Felder;
  recipe_sketch?: unknown; beispiel_attachment_refs?: AttRef[];
  provenienz?: Record<string, string>; note?: string; bridge_automations?: unknown;
}
interface DocTyp {
  typ: string; volumen_6m: number; volumen_quelle: string; verarbeitungspfad: string;
  zustaendige_karten?: string[]; familien?: string[]; sf_bestand?: string;
}
interface Grundlast {
  emails: number; minutes_per_email: number; hours_6mo: number; hours_per_year: number;
  hinweis?: string;
}
// v4-Modell: das Atom der Nachfrage (411). Trägt Prozess-/Automatisierungs-Zuordnung
// als Attribute → direkte Quelle der flachen Aufgaben-Tabelle + Gruppierungen.
interface Aufgabe {
  id: string; name: string; name_de: string; dept: string;
  act: string; act_label: string; hebel: string;
  freq_6mo: number; handling_hours_6mo: number; handling_hours_per_year: number;
  saved_hours_6mo: number; automatisierbar_hours_per_year: number;
  coverage: Coverage | null;
  automation_uc_id: string;         // == use_case_key (uc-xxx) der übernehmenden Automatisierung
  prozess_id: string;               // Familie (LA-F2 …) ODER "ohne_zuordnung"
  prozesskette: string;             // Kette (LA/OE/EF) ODER "ohne_zuordnung"
}
interface ProzessOhneZuordnung {
  prozess_id: string; label: string; hours_per_year: number;
  automatisierbar_hours_per_year: number; aufgaben_count: number; dept_anteile?: DeptAnteile;
}
// Agent-Reife-Attribute je Automatisierung (nur atlas_agent-Hebel; 47 Einträge),
// aus dem stillgelegten Roadmap-Cluster-Modell übernommen.
interface AgentAttr {
  stufe: number; stufen: number[]; capabilities: string[]; cluster_ids: string[];
  aufgaben_count: number;
  recipe_kurz: { cluster_id: string; trigger: string; n_steps: number; end_states: string[] }[];
}
interface BoardV3 {
  kpis: {
    use_cases: number; clusters: number; total_saved_hours_6mo: number;
    hours_by_lever: Record<string, number>; hours_by_dept: Record<string, number>;
    // Doppel-Kennzahl: Aufwand HEUTE (volle Handling-Zeit) — autom. = davon
    // automatisierbar (die ``*_saved``/``hours_by_*``-Werte oben).
    total_handling_hours_6mo: number;
    handling_by_lever: Record<string, number>; handling_by_dept: Record<string, number>;
    // Posteingangs-Grundlast (Lese-/Triage-Zeit ALLER Mails) — SEPARAT, NICHT in
    // den Aufgaben-Aufwänden enthalten.
    grundlast: Grundlast;
    // v4: eine definierte Zählregel für offene Entscheidungen (meta.zaehlregel_…).
    offene_entscheidungen?: number;
  };
  prerequisites: PrereqV3[];
  quick_wins: QuickWinV3[];
  prozessketten: Prozesskette[];
  doc_typ_map: DocTyp[];
  digitalization_specs: DigiSpec[];
  crosswalk: {
    artifact_family_to_doc_typ: Record<string, { doc_typ: string; methode?: string }>;
    dept_anteile: { doc_typ: Record<string, DeptAnteile>; familie: Record<string, DeptAnteile>; kette: Record<string, DeptAnteile> };
  };
  agent_roadmap: { stufen: Stufe[]; clusters?: Cluster[]; kernbefund?: string };
  // v4-Modell-Felder:
  aufgaben: Aufgabe[];
  prozess_ohne_zuordnung: ProzessOhneZuordnung;
  automation_agent_attrs: Record<string, AgentAttr>;
}

// ── UC-Katalog-Overview (Spiegel /email-automation) ──────────────────────────
// Ein Member-Cluster = eines der 411 eindeutigen Task-Muster (das „Arbeits-Korn").
// name_de + coverage kommen aus dem angereicherten Dataset (JSONB, kein Hardcode).
export interface MemberOverview {
  id: string; name: string | null; name_de: string | null; name_de_source?: string | null;
  freq: number | null; hours_6mo: number | null; act: string | null; act_label: string | null;
  coverage: Coverage | null;
  // Aufwand HEUTE (volle Handling-Zeit) je Muster; hours_6mo = davon automatisierbar.
  handling_hours_6mo: number | null;
}
interface UseCaseOverview {
  id: string; use_case_key: string; name: string | null; dept: string | null; lead: string | null;
  artifact_family: string | null; human_only: boolean | null;
  quick_win: boolean | null; effort: string | null; platform: string | null; pattern: string | null;
  freq_6mo: number | null; hours_6mo: number | null; hours_per_year: number | null;
  coverage: Coverage | null;
  // Die Member-Cluster der Aufgabe (für die Einzel-Ansicht + act_mixed-Ableitung).
  member_clusters: MemberOverview[] | null;
}

// ═════════════════════════════════════════════════════════════════════════════
// Konstanten — Hebel, Abteilungen, Capabilities, deutsche Labels
// ═════════════════════════════════════════════════════════════════════════════
const DEPTS = ["EF", "SCM", "Logistics", "Purchasing"] as const;

const LEVER_ORDER = ["portal", "flow", "atlas_agent", "atlas_faq", "atlas_template", "human_only"] as const;
const LEVER_LABEL: Record<string, string> = {
  portal: "Portal", flow: "SF-Flow", atlas_agent: "Atlas-Agent",
  atlas_faq: "Atlas-FAQ", atlas_template: "Atlas-Template", human_only: "Bleibt manuell",
};
// Hebel-Chips sind NEUTRAL (Farbdisziplin v8: Petrol ist die einzige
// Interaktionsfarbe; kein lila/mint je Hebel-Typ) — Umsetzung via <Chip variant="hebel">.
const PLAT_TO_LEVER: Record<string, string> = {
  "SF-Flow": "flow", "Email-Service+Atlas": "atlas_agent",
  "Atlas-Agent": "atlas_agent", Portal: "portal",
};
function cardLever(qw: QuickWinV3): string {
  return qw.hebel || PLAT_TO_LEVER[qw.plattform] || "flow";
}
// Capability → blockierende PR-IDs (aus board.prerequisites.unlocked_capability).
const CAP_HARD = new Set(["mail_send", "doc_extract", "portal"]);

// Verarbeitungspfad-Enum → deutsch.
const PATH_LABEL: Record<string, string> = {
  create_document: "Dokument erzeugen", confirm: "Bestätigen", negotiate: "Aushandeln",
  reconcile: "Abgleichen", forward_only: "Weiterleiten", extract_and_enter: "Extrahieren → System",
  extract_and_reconcile: "Extrahieren + Abgleich", chase_and_collect: "Nachfassen + Einsammeln",
  review_and_approve: "Review + Freigabe", sanity_check: "Sanity-Check", sign: "Signieren",
};
function pathLabel(p?: string): string { return p ? (PATH_LABEL[p] ?? p) : "—"; }

const CAP_LABEL: Record<string, string> = {
  mail_send: "Mail-Versand", doc_extract: "Doc-Extraktion", portal: "Portal-Upload",
  sf_read: "SF lesen", sf_write: "SF schreiben", task_reminder: "Reminder",
  notify: "Benachrichtigen", classify: "Klassifizieren", ocr: "OCR",
};
function capLabel(cap: string): string { return CAP_LABEL[cap] ?? cap.replace(/_/g, " "); }
function capBlocked(cap: string): boolean { return CAP_HARD.has(cap); }

// ═════════════════════════════════════════════════════════════════════════════
// Route-Pfad-Helfer (echte Routen; Filter-QS wird mitgefuehrt)
// ═════════════════════════════════════════════════════════════════════════════
// v9-Leitmodell: DREI Ansichten entlang der drei Fragen — Aufgaben (wo steckt die
// Arbeit) · Automatisierungen (was bauen wir) · Entscheiden (was ist als Nächstes dran).
export type Seg = "aufgaben" | "automatisierungen" | "entscheiden";
export const SEGMENTS: [Seg, string][] = [
  ["aufgaben", "Aufgaben"], ["automatisierungen", "Automatisierungen"], ["entscheiden", "Entscheiden"],
];
// normSeg bildet auch die Alt-Segmente ab (Deep-Links brechen nicht): die
// Prozessketten-Linse wird eine Aufgaben-Gruppierung, Hebel + Roadmap gehen in
// die Automatisierungen-Ansicht.
export function normSeg(raw: string | null): Seg {
  if (raw === "aufgaben" || raw === "automatisierungen" || raw === "entscheiden") return raw;
  if (raw === "entscheidungen") return "entscheiden";
  if (raw === "hebel" || raw === "roadmap" || raw === "agent") return "automatisierungen";
  if (raw === "prozess") return "aufgaben";       // + group=kette (Redirect-Effekt)
  return "aufgaben";  // Default-Ansicht
}
// Nur die Filter-Params (seg/dept/lever) — Drill lebt im Pfad, nicht im QS.
export function buildFilterQs(seg: Seg, dept: string | null, lever: string | null): string {
  const u = new URLSearchParams();
  if (seg !== "aufgaben") u.set("seg", seg);  // Default-Ansicht → kein seg-Param
  if (dept) u.set("dept", dept);
  if (lever) u.set("lever", lever);
  return u.toString();
}
const qsSuffix = (qs: string) => (qs ? `?${qs}` : "");
export const l1Path = (qs: string) => `/email-automation${qsSuffix(qs)}`;
const pPath = (fam: string, qs: string) => `/email-automation/p/${fam}${qsSuffix(qs)}`;
const tPath = (fam: string, doc: string, qs: string) => `/email-automation/p/${fam}/t/${doc}${qsSuffix(qs)}`;
const decidePath = (id: string, qs: string) => `/email-automation/decide/${id}${qsSuffix(qs)}`;
const ucPath = (id: string, qs: string) => `/email-automation/${id}${qsSuffix(qs)}`;
// Automatisierungs-Detail (board-getrieben, per use_case_key uc-xxx).
const aPath = (ucKey: string, qs: string) => `/email-automation/a/${encodeURIComponent(ucKey)}${qsSuffix(qs)}`;
// Ansicht-Wechsel-Pfad, der eine Aufgaben-Gruppierung mitgibt (für Alt-Segment-Redirects).
const l1PathGrouped = (qs: string, group: string) => {
  const u = new URLSearchParams(qs);
  u.set("group", group);
  return `/email-automation${qsSuffix(u.toString())}`;
};

// ═════════════════════════════════════════════════════════════════════════════
// UC-Katalog-Hook + effektiver Status-Hook
// ═════════════════════════════════════════════════════════════════════════════
let _ucCache: UseCaseOverview[] | null = null;
function useUseCases(): { list: UseCaseOverview[]; loaded: boolean } {
  const [list, setList] = useState<UseCaseOverview[]>(_ucCache ?? []);
  const [loaded, setLoaded] = useState<boolean>(_ucCache !== null);
  useEffect(() => {
    if (_ucCache) return;
    let cancelled = false;
    api.get<UseCaseOverview[]>("/email-automation")
      .then((d) => { _ucCache = d; if (!cancelled) { setList(d); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);
  return { list, loaded };
}

function useEffStatus(): (cardId: string) => CardStatus {
  const { decisions } = useDecisions(true);
  const { statuses } = useCardStatuses(true);
  const map = useMemo(() => new Map(statuses.map((s: CardStatusRow) => [s.card_id, s.status])), [statuses]);
  const decided = useMemo(() => new Set(decisions.map((d: Decision) => d.card_id)), [decisions]);
  return (cardId: string) => map.get(cardId) ?? (decided.has(cardId) ? "entschieden" : "offen");
}

// ═════════════════════════════════════════════════════════════════════════════
// Gemeinsame kleine Bausteine
// ═════════════════════════════════════════════════════════════════════════════
function hoursYear(h6mo: number | null | undefined): number { return Math.round((h6mo ?? 0) * 2); }

function StatusPill({ status }: { status: CardStatus }) {
  // Farbdisziplin: „in Bau" = Petrol-Tint statt Amber; kein Gelb.
  const cls: Record<CardStatus, string> = {
    offen: "bg-surface-3 text-text-2",
    entschieden: "bg-accent-50 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300",
    in_bau: "bg-accent-50 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300",
    live: "bg-success-soft text-success",
    wirkung_gemessen: "bg-success text-white",
  };
  return <span className={clsx("inline-flex items-center px-2 py-0.5 rounded-chip text-[0.7rem] font-semibold whitespace-nowrap", cls[status])}>{STATUS_LABEL[status]}</span>;
}
function LeverBadge({ lever }: { lever: string }) {
  return <Chip variant="hebel">{LEVER_LABEL[lever] ?? lever}</Chip>;
}
function EffortPill({ effort }: { effort?: string | null }) {
  if (!effort) return null;
  return <span className="inline-flex items-center justify-center px-1.5 rounded-full border border-border-strong text-[0.68rem] font-semibold text-text-2 whitespace-nowrap">{effort.toUpperCase()}</span>;
}
function AmpelDot({ a }: { a?: Ampel }) {
  // Farbdisziplin v8: Status als DS-Chip statt Ampel-Glyph (kein ◐/○).
  if (a === "gruen") return <Chip variant="status" tone="success">läuft</Chip>;
  if (a === "gelb") return <Chip variant="status" tone="petrol">teilweise</Chip>;
  return <Chip variant="status" tone="neutral">Lücke</Chip>;
}
function DeptShareBar({ da }: { da?: DeptAnteile }) {
  if (!da || !da.total_hours_per_year) return null;
  const entries = DEPTS.map((d) => [d, da.share[d] ?? 0] as const).filter(([, v]) => v > 0);
  if (!entries.length) return null;
  const tone: Record<string, string> = { EF: "bg-brand-500", SCM: "bg-accent-500", Logistics: "bg-warning", Purchasing: "bg-slate-400" };
  return (
    <div className="mt-2">
      <div className="flex h-2 rounded-full overflow-hidden border border-border">
        {entries.map(([d, v]) => <div key={d} className={clsx(tone[d] ?? "bg-slate-400")} style={{ width: `${v * 100}%` }} title={`${d} ${(v * 100).toFixed(0)}%`} />)}
      </div>
      <div className="mt-1 flex gap-2.5 flex-wrap text-[0.68rem] text-text-3">
        {entries.map(([d, v]) => (
          <span key={d} className="inline-flex items-center gap-1"><span className={clsx("w-2 h-2 rounded-sm", tone[d] ?? "bg-slate-400")} />{d} {(v * 100).toFixed(0)}%</span>
        ))}
      </div>
    </div>
  );
}
function SortLabel({ children }: { children: ReactNode }) {
  return <span className="text-[0.7rem] text-text-3 whitespace-nowrap">Sortierung: {children}</span>;
}
// Dezenter horizontaler Vergleichsbalken (h/Jahr relativ zum Listen-Maximum) —
// ruhig, einfarbig Petrol-Tint, kein Farbzirkus (Befund 7 / AK 9).
function HoursBar({ value, max, className }: { value: number; max: number; className?: string }) {
  if (!max || value <= 0) return null;
  const pct = Math.max(3, Math.min(100, Math.round((value / max) * 100)));
  return (
    <div className={clsx("h-1.5 rounded-full bg-bar-track overflow-hidden", className)} aria-hidden="true">
      <div className="h-full rounded-full bg-bar-fill/55" style={{ width: `${pct}%` }} />
    </div>
  );
}
function BlockCard({ title, accent, right, children }: { title: string; accent?: boolean; right?: ReactNode; children: ReactNode }) {
  return (
    <Card padding="none" className={clsx("p-4", accent && "border-l-2 border-l-brand-500")}>
      <div className="flex items-center gap-2 mb-2.5">
        <div className="ea-section text-text-3">{title}</div>
        {right && <div className="ml-auto">{right}</div>}
      </div>
      {children}
    </Card>
  );
}
// Sektions-Sprungleiste unter dem Kopfband — scrollt im Content-Outlet zu einer
// Sektion (AK 4: Sekundaeres ≤1 Klick erreichbar).
function JumpBar({ items }: { items: { id: string; label: string }[] }) {
  const go = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <nav aria-label="Abschnitte" className="mb-5 flex flex-wrap items-center gap-1.5">
      <span className="ea-section text-text-3 mr-1">Abschnitte</span>
      {items.map((it) => (
        <button key={it.id} type="button" onClick={() => go(it.id)}
          className="px-2.5 py-1 rounded-full border border-border bg-surface text-[0.78rem] text-text-2 hover:border-brand-500 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors">
          {it.label}
        </button>
      ))}
    </nav>
  );
}
function Section({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="mt-5 first:mt-0">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="ea-section text-text-3">{title}</span>
        {right}
      </div>
      {children}
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Root: Route-Dispatch (Drill via useParams) + Segment/Filter (Query-Params)
// ═════════════════════════════════════════════════════════════════════════════
export function EmailAutomationHierarchy({ board: rawBoard }: { board: unknown }) {
  const board = rawBoard as BoardV3;
  const { prozessId, docTyp, leverId, stufeNr, clusterId, ucKey } = useParams();
  const [params] = useSearchParams();
  const rawSeg = params.get("seg") ?? params.get("view");
  const seg = normSeg(rawSeg);
  const dept = params.get("dept");
  const lever = params.get("lever");
  const filterQs = buildFilterQs(seg, dept, lever);

  // ── Alt-Segment-Redirects (Deep-Links brechen nicht) ────────────────────────
  // Prozessketten-Segment → Aufgaben mit Gruppierung nach Kette. Hebel/Roadmap-
  // Segment → Automatisierungen. Alt-Drill-Routen /hebel /roadmap → Automatisierungen
  // (Hebel als Filter, Roadmap-Rezepte im Automatisierungs-Detail). /p bleibt.
  if (rawSeg === "prozess") return <Navigate to={l1PathGrouped(filterQs, "kette")} replace />;
  if (leverId) return <Navigate to={l1Path(buildFilterQs("automatisierungen", dept, leverId))} replace />;
  if (stufeNr) return <Navigate to={l1Path(buildFilterQs("automatisierungen", dept, lever))} replace />;
  if (clusterId) {
    // Rezept lebt jetzt im Detail der Automatisierung, die diesen Cluster übernimmt.
    const owner = board.aufgaben.find((a) => a.id === clusterId);
    return <Navigate to={owner ? aPath(owner.automation_uc_id, filterQs) : l1Path(buildFilterQs("automatisierungen", dept, lever))} replace />;
  }

  // ── Drill-Dispatch: Vollseiten ueber echte Routen ───────────────────────────
  // Kopfband (Titel · Ansicht-Tabs · Filter) liefert die persistente App-Shell auf
  // JEDER Route; hier lebt nur der Content-Outlet: Drill-Vollseiten ODER die L1.
  if (ucKey) return <AutomationDetail board={board} ucKey={ucKey} qs={filterQs} />;
  if (prozessId && docTyp) return <TaskL3 board={board} famId={prozessId} docTyp={docTyp} qs={filterQs} dept={dept} />;
  if (prozessId) return <ProzessL2 board={board} famId={prozessId} qs={filterQs} dept={dept} lever={lever} />;

  // ── L1-Content (Kernfrage je Ansicht) ───────────────────────────────────────
  return (
    <div>
      {seg === "aufgaben" && <AufgabenL1 board={board} dept={dept} lever={lever} />}
      {seg === "automatisierungen" && <AutomatisierungenL1 board={board} dept={dept} lever={lever} />}
      {seg === "entscheiden" && <EntscheidungenL1 board={board} dept={dept} lever={lever} qs={filterQs} />}
    </div>
  );
}

export function FilterBar({ seg, dept, lever, onToggle }: {
  seg: Seg; dept: string | null; lever: string | null;
  onToggle: (k: "dept" | "lever", v: string) => void;
}) {
  // Filter-Chip = Pille direkt auf dem Canvas (kein grauer Kasten). Aktiv = Petrol.
  const FChip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
    <button onClick={onClick}
      className={clsx("px-3 py-1 rounded-full text-[0.78rem] font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active ? "bg-brand-500 border-brand-500 text-white" : "bg-surface border-border text-text-2 hover:border-brand-500")}>{children}</button>
  );
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-3 mr-1">Abteilung</span>
        <FChip active={!dept} onClick={() => dept && onToggle("dept", dept)}>Alle</FChip>
        {DEPTS.map((d) => <FChip key={d} active={dept === d} onClick={() => onToggle("dept", d)}>{d}</FChip>)}
      </div>
      {seg !== "entscheiden" && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-3 mr-1">Hebel</span>
          <FChip active={!lever} onClick={() => lever && onToggle("lever", lever)}>Alle</FChip>
          {LEVER_ORDER.map((l) => <FChip key={l} active={lever === l} onClick={() => onToggle("lever", l)}>{LEVER_LABEL[l]}</FChip>)}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ═════════════════════════════════════════════════════════════════════════════
// Aufgaben — L1: flache 411er-Tabelle (Aufwand absteigend) mit Gruppierungs-
//   Umschalter keine | Prozesskette | Automatisierung (Gruppenkopf-Summen, R2).
//   Quelle: board.aufgaben (das v4-Atom). Filter: Abteilung + Hebel (Shell-Chips)
//   + Freitext. Zeile → Automatisierungs-Detail (R5: die übernehmende
//   Automatisierung ist EIN Klick). Die Grundlast bleibt separat als Subline.
// ═════════════════════════════════════════════════════════════════════════════
type AufGroupMode = "none" | "kette" | "automatisierung";
function normGroup(raw: string | null): AufGroupMode {
  return raw === "kette" || raw === "automatisierung" ? raw : "none";
}
const KETTE_LABEL: Record<string, string> = {
  LA: "Lieferabwicklung", OE: "Order-Entry", EF: "Externe Fertigung",
  ohne_zuordnung: "ohne Prozessketten-Bezug — abteilungsinterne Bearbeitung",
};
// Feste Spaltenreihenfolge (Badge-Raster v7): Aufgabe · Abt. · Hebel · Vorg./6M ·
// Aufwand h/J (ValueBar) · autom. h/J · Status.
const AUF_COLS = "minmax(0,1fr) 54px 128px 76px 152px 82px 96px";

// Bestands-Status als DS-Chip (none→offen · partial→teilweise · covered→abgedeckt).
function CoverageStatusChip({ coverage }: { coverage: Coverage | null | undefined }) {
  const s = (coverage?.status || "").toLowerCase();
  if (s === "covered_auto" || s === "covered_manual") return <Chip variant="status" tone="success">abgedeckt</Chip>;
  if (s === "partial") return <Chip variant="status" tone="petrol">teilweise</Chip>;
  return <Chip variant="status" tone="neutral">offen</Chip>;
}

// Sortierbarer Spaltenkopf (Richtung in aria-sort + Pfeil-Indikator).
function SortHeader<K extends string>({ label, col, sortKey, dir, onSort, align = "left", className }: {
  label: string; col: K; sortKey: K; dir: "asc" | "desc";
  onSort: (k: K) => void; align?: "left" | "right"; className?: string;
}) {
  const active = sortKey === col;
  return (
    <button type="button" onClick={() => onSort(col)}
      aria-label={`Nach ${label} sortieren`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      className={clsx(
        "inline-flex items-center gap-1 uppercase tracking-wide text-[0.68rem] font-semibold rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        active ? "text-brand-500" : "text-text-3 hover:text-text-2",
        align === "right" && "justify-end",
        className,
      )}>
      <span>{label}</span>
      {active
        ? (dir === "asc" ? <ArrowUp className="w-3 h-3 shrink-0" /> : <ArrowDown className="w-3 h-3 shrink-0" />)
        : <ArrowDown className="w-3 h-3 shrink-0 opacity-25" />}
    </button>
  );
}

// use_case_key (uc-xxx) → Automatisierungs-Overview (Name/UUID/Hebel) aus den 186.
function useAutomationByKey(): { byKey: Map<string, UseCaseOverview>; loaded: boolean } {
  const { list, loaded } = useUseCases();
  const byKey = useMemo(() => new Map(list.map((u) => [u.use_case_key, u] as const)), [list]);
  return { byKey, loaded };
}

// Primär-Familie je Kette (die Familie mit den meisten h/Jahr) — Ziel des
// klickbaren Prozessketten-Gruppenkopfs (die Prozess-Detailseite bleibt erreichbar).
function usePrimaryFamOfChain(board: BoardV3): Map<string, string> {
  return useMemo(() => {
    const m = new Map<string, string>();
    board.prozessketten.forEach((k) => {
      const top = [...k.familien].sort((a, b) => (b.hours_per_year ?? 0) - (a.hours_per_year ?? 0))[0];
      if (top) m.set(k.chain_id, top.family_id);
    });
    return m;
  }, [board]);
}

type AufSortKey = "name" | "dept" | "freq" | "aufwand" | "autom";
const AUF_SORT_KEYS: AufSortKey[] = ["name", "dept", "freq", "aufwand", "autom"];
function aufCmp(a: Aufgabe, b: Aufgabe, key: AufSortKey): number {
  switch (key) {
    case "name": return (a.name_de || a.name).localeCompare(b.name_de || b.name, "de");
    case "dept": return (a.dept || "").localeCompare(b.dept || "", "de");
    case "freq": return (a.freq_6mo ?? 0) - (b.freq_6mo ?? 0);
    case "aufwand": return a.handling_hours_per_year - b.handling_hours_per_year;
    case "autom": return a.automatisierbar_hours_per_year - b.automatisierbar_hours_per_year;
  }
}

function AufgabenL1({ board, dept, lever }: {
  board: BoardV3; dept: string | null; lever: string | null;
}) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const group = normGroup(params.get("group"));
  const [q, setQ] = useState("");
  const eff = useEffStatus();
  const { byKey } = useAutomationByKey();
  const primaryFam = usePrimaryFamOfChain(board);

  const rawSort = params.get("sort");
  const sortKey: AufSortKey = AUF_SORT_KEYS.includes(rawSort as AufSortKey) ? (rawSort as AufSortKey) : "aufwand";
  const dir: "asc" | "desc" = params.get("dir") === "asc" ? "asc" : "desc";
  const setSort = (key: AufSortKey) => {
    const u = new URLSearchParams(params);
    if (key === sortKey) u.set("dir", dir === "asc" ? "desc" : "asc");
    else { u.set("sort", key); u.set("dir", key === "name" || key === "dept" ? "asc" : "desc"); }
    setParams(u, { replace: true });
  };
  const setGroup = (gm: AufGroupMode) => {
    const u = new URLSearchParams(params);
    if (gm === "none") u.delete("group"); else u.set("group", gm);
    setParams(u, { replace: true });
  };

  // KPI-Reihe = die eine Buchhaltung (Basiszahlen, ungefiltert): Aufwand 5.652 ·
  // automatisierbar 2.744 · offene Entscheidungen (Zählregel meta) · Aufgaben 411.
  const aufwandY = hoursYear(board.kpis.total_handling_hours_6mo);
  const autoY = hoursYear(board.kpis.total_saved_hours_6mo);
  const prsOpen = board.prerequisites.filter((p) => p.blocking === "hard" && p.status !== "done").length;
  const cardsOpen = board.quick_wins.filter((w) => eff(w.id) === "offen").length;
  const offeneEntsch = prsOpen + cardsOpen;   // Basiswert 22, reduziert um Live-Entscheidungen (Zählregel)
  const g = board.kpis.grundlast;

  const query = q.trim().toLowerCase();
  const filtered = useMemo(() => board.aufgaben.filter((a) => {
    if (dept && a.dept !== dept) return false;
    if (lever && a.hebel !== lever) return false;
    if (query && !`${a.name_de || ""} ${a.name || ""} ${a.id}`.toLowerCase().includes(query)) return false;
    return true;
  }), [board.aufgaben, dept, lever, query]);

  const maxHy = Math.max(1, ...filtered.map((a) => a.handling_hours_per_year));
  const rowQs = params.toString();
  const goAuto = (a: Aufgabe) => navigate(aPath(a.automation_uc_id, rowQs));
  const filteredSum = Math.round(filtered.reduce((s, a) => s + a.handling_hours_per_year, 0));

  // Gruppen bauen (nach Kette / Automatisierung) mit Summe je Gruppe.
  type Grp = { key: string; label: string; sum: number; count: number; onOpen?: () => void; hint?: string; rows: Aufgabe[] };
  const groups: Grp[] = useMemo(() => {
    if (group === "none") return [];
    const map = new Map<string, Aufgabe[]>();
    filtered.forEach((a) => {
      const k = group === "kette" ? a.prozesskette : a.automation_uc_id;
      const arr = map.get(k) || []; arr.push(a); map.set(k, arr);
    });
    const out: Grp[] = Array.from(map.entries()).map(([k, rows]) => {
      const sum = Math.round(rows.reduce((s, a) => s + a.handling_hours_per_year, 0));
      const sorted = [...rows].sort((a, b) => b.handling_hours_per_year - a.handling_hours_per_year);
      if (group === "kette") {
        const isOhne = k === "ohne_zuordnung";
        const fam = !isOhne ? primaryFam.get(k) : undefined;
        return {
          key: k, label: KETTE_LABEL[k] ?? k, sum, count: rows.length, rows: sorted,
          onOpen: fam ? () => navigate(pPath(fam, rowQs)) : undefined,
          hint: isOhne ? undefined : "Prozess-Detail öffnen",
        };
      }
      const uc = byKey.get(k);
      return {
        key: k, label: uc?.name || k, sum, count: rows.length, rows: sorted,
        onOpen: () => navigate(aPath(k, rowQs)), hint: "Automatisierung öffnen",
      };
    });
    out.sort((a, b) => b.sum - a.sum);
    return out;
  }, [filtered, group, byKey, primaryFam, rowQs, navigate]);

  const flat = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const c = aufCmp(a, b, sortKey);
      if (c !== 0) return dir === "asc" ? c : -c;
      return b.handling_hours_per_year - a.handling_hours_per_year;
    });
    return arr;
  }, [filtered, sortKey, dir]);

  const GBtn = ({ gm, label }: { gm: AufGroupMode; label: string }) => (
    <button type="button" role="tab" aria-selected={group === gm} onClick={() => setGroup(gm)}
      className={clsx("px-3 py-1.5 text-[0.8rem] font-medium shrink-0 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
        group === gm ? "bg-brand-500 text-white" : "bg-surface hover:bg-surface-2 text-text-2")}>{label}</button>
  );

  const AufRow = ({ a }: { a: Aufgabe }) => (
    <Trow cols={AUF_COLS} onClick={() => goAuto(a)} label={a.name_de || a.name}>
      <span className="text-[0.84rem] font-medium text-text leading-snug line-clamp-2 pr-2" title={a.name || a.name_de}>{a.name_de || a.name}</span>
      <span className="text-[0.82rem] text-text-2">{a.dept ?? "—"}</span>
      <span>{a.hebel ? <LeverBadge lever={a.hebel} /> : <span className="text-[0.72rem] text-text-3">—</span>}</span>
      <span className="text-right text-[0.84rem] text-text-2 tabular-nums">{fmtInt(a.freq_6mo)}</span>
      <ValueBar value={Math.round(a.handling_hours_per_year)} max={maxHy} className="w-full justify-self-end" />
      <span className="text-right text-[0.82rem] text-text-3 tabular-nums">{a.automatisierbar_hours_per_year > 0 ? fmtInt(Math.round(a.automatisierbar_hours_per_year)) : "—"}</span>
      <span><CoverageStatusChip coverage={a.coverage} /></span>
    </Trow>
  );

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mb-3">
        <KpiCard value={fmtInt(aufwandY)} label="h/Jahr Aufwand" />
        <KpiCard value={fmtInt(autoY)} label="h/J automatisierbar" />
        <KpiCard value={fmtInt(offeneEntsch)} label="offene Entscheidungen" />
        <KpiCard value={fmtInt(board.aufgaben.length)} label="Aufgaben" />
      </div>
      {g && g.hours_per_year ? (
        <div className="mb-4 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[0.8rem] text-text-3 px-0.5">
          <span className="text-brand-500 font-semibold">+ Posteingangs-Grundlast ≈ {fmtInt(g.hours_per_year)} h/Jahr</span>
          <span>({fmtInt(g.emails)} Emails × {String(g.minutes_per_email).replace(".", ",")} min Lesen/Triage) — von keiner Automatisierung erfasst</span>
        </div>
      ) : null}

      {/* Gruppierungs-Umschalter + Suche + Summenzeile */}
      <div className="mb-3 flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-x-2.5 gap-y-1.5 flex-wrap">
          <span className="text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-3">Gruppierung</span>
          <div className="inline-flex rounded-lg border border-border overflow-hidden" role="tablist" aria-label="Aufgaben-Gruppierung">
            <GBtn gm="none" label="keine" />
            <GBtn gm="kette" label="Prozesskette" />
            <GBtn gm="automatisierung" label="Automatisierung" />
          </div>
        </div>
        <div className="relative w-full lg:w-80">
          <Search className="w-4 h-4 text-text-3 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Aufgabe suchen (deutsch/englisch) …" aria-label="Aufgabe suchen"
            className="w-full rounded-control border border-border bg-surface pl-8 pr-3 py-2 text-sm text-text placeholder:text-text-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" />
        </div>
      </div>
      <div className="mb-3 flex items-baseline gap-x-2 gap-y-1 flex-wrap text-[0.8rem] text-text-3 px-0.5">
        <span className="ea-num font-semibold text-text tabular-nums">{fmtInt(filtered.length)}</span>
        <span>von {fmtInt(board.aufgaben.length)} Aufgaben</span>
        <span>·</span>
        <span>Aufwand <span className="ea-num text-text font-semibold tabular-nums">≈ {fmtInt(filteredSum)}</span> h/Jahr</span>
        {group !== "none" && <><span>·</span><span>{groups.length} Gruppen — Summe der Gruppenköpfe = <span className="ea-num text-text font-semibold tabular-nums">{fmtInt(filteredSum)}</span> h/Jahr</span></>}
      </div>

      <DataTable>
        <div className="overflow-x-auto">
          <div className="min-w-[880px]">
            <Thead cols={AUF_COLS}>
              {group === "none"
                ? <SortHeader label="Aufgabe" col="name" sortKey={sortKey} dir={dir} onSort={setSort} />
                : <Th>Aufgabe</Th>}
              {group === "none" ? <SortHeader label="Abt." col="dept" sortKey={sortKey} dir={dir} onSort={setSort} /> : <Th>Abt.</Th>}
              <Th>Hebel</Th>
              {group === "none"
                ? <SortHeader label="Vorg./6M" col="freq" sortKey={sortKey} dir={dir} onSort={setSort} align="right" className="justify-end" />
                : <Th align="right">Vorg./6M</Th>}
              {group === "none"
                ? <SortHeader label="Aufwand h/J" col="aufwand" sortKey={sortKey} dir={dir} onSort={setSort} align="right" className="justify-end" />
                : <Th align="right">Aufwand h/J</Th>}
              {group === "none"
                ? <SortHeader label="autom. h/J" col="autom" sortKey={sortKey} dir={dir} onSort={setSort} align="right" className="justify-end" />
                : <Th align="right">autom. h/J</Th>}
              <Th>Status</Th>
            </Thead>

            {filtered.length === 0 && <div className="px-[18px] py-6 text-sm text-text-3">Keine Aufgaben für diese Auswahl.</div>}

            {group === "none" && flat.map((a) => <AufRow key={a.id} a={a} />)}

            {group !== "none" && groups.map((grp) => (
              <div key={grp.key}>
                <div
                  onClick={grp.onOpen}
                  {...(grp.onOpen ? keyActivate(grp.onOpen) : {})}
                  className={clsx("grid items-center gap-2.5 px-[18px] py-2.5 bg-surface-2 border-t border-border",
                    grp.onOpen && "cursor-pointer hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500")}
                  style={{ gridTemplateColumns: AUF_COLS }}>
                  <span className="flex items-center gap-1.5 min-w-0">
                    {grp.onOpen && <ChevronRight className="w-4 h-4 text-text-3 shrink-0" />}
                    <span className="text-[0.74rem] font-bold uppercase tracking-[0.05em] text-text-2 truncate" title={grp.label}>{grp.label}</span>
                    <span className="text-[0.7rem] text-text-3 normal-case shrink-0">· {grp.count} Aufg.</span>
                    {grp.hint && <span className="text-[0.66rem] text-brand-500 normal-case shrink-0 hidden sm:inline">{grp.hint} →</span>}
                  </span>
                  <span /><span /><span />
                  <span className="ea-num text-right text-[0.86rem] font-semibold text-brand-500 tabular-nums">~{fmtInt(grp.sum)} h/J</span>
                  <span /><span />
                </div>
                {grp.rows.map((a) => <AufRow key={a.id} a={a} />)}
              </div>
            ))}
          </div>
        </div>
      </DataTable>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Automatisierungen — L1: die 186 als Pipeline nach Status (offen → entschieden →
//   in Bau → live → Wirkung gemessen), zzgl. „bleibt manuell" (bewusste Nicht-
//   Automatisierung, damit der Rest-Aufwand ehrlich bleibt). Filter: Hebel +
//   Abteilung (Shell) + Stufe (lokal). Zeile → Automatisierungs-Detail.
// ═════════════════════════════════════════════════════════════════════════════
type AutoStatus = CardStatus | "bleibt_manuell";
const AUTO_STATUS_ORDER: AutoStatus[] = [...STATUS_STEPS, "bleibt_manuell"];
const AUTO_STATUS_LABEL: Record<AutoStatus, string> = { ...STATUS_LABEL, bleibt_manuell: "bleibt manuell" };
const AUTO_COLS = "minmax(0,1fr) 54px 128px 58px 152px 82px";
interface AutoRow {
  uc: UseCaseOverview; key: string; name: string; dept: string | null; hebel: string;
  aufwand: number; aufwandRaw: number; autom: number; count: number; stufe?: number; status: AutoStatus;
}

function AutomatisierungenL1({ board, dept, lever }: {
  board: BoardV3; dept: string | null; lever: string | null;
}) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { list, loaded } = useUseCases();
  const eff = useEffStatus();
  const [q, setQ] = useState("");
  const stufeF = params.get("stufe");
  const agent = board.automation_agent_attrs;

  const aufByUc = useMemo(() => {
    const m = new Map<string, Aufgabe[]>();
    board.aufgaben.forEach((a) => { const arr = m.get(a.automation_uc_id) || []; arr.push(a); m.set(a.automation_uc_id, arr); });
    return m;
  }, [board.aufgaben]);

  const rows: AutoRow[] = useMemo(() => list.map((uc) => {
    const key = uc.use_case_key;
    const taken = aufByUc.get(key) || [];
    const aufwandRaw = taken.reduce((s, a) => s + a.handling_hours_per_year, 0);
    const aufwand = Math.round(aufwandRaw);
    const autom = Math.round(taken.reduce((s, a) => s + a.automatisierbar_hours_per_year, 0));
    const hebel = uc.lead || "";
    const isManual = hebel === "human_only";
    const status: AutoStatus = isManual ? "bleibt_manuell" : eff(key);
    return { uc, key, name: uc.name || key, dept: uc.dept, hebel, aufwand, aufwandRaw, autom, count: taken.length, stufe: agent[key]?.stufe, status };
  }), [list, aufByUc, eff, agent]);

  const query = q.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (dept && r.dept !== dept) return false;
    if (lever && r.hebel !== lever) return false;
    if (stufeF && String(r.stufe ?? "") !== stufeF) return false;
    if (query && !`${r.name} ${r.key}`.toLowerCase().includes(query)) return false;
    return true;
  });

  const groups = AUTO_STATUS_ORDER
    .map((st) => ({ st, rows: filtered.filter((r) => r.status === st).sort((a, b) => b.aufwand - a.aufwand) }))
    .filter((grp) => grp.rows.length > 0);
  const maxHy = Math.max(1, ...filtered.map((r) => r.aufwand));
  const totalAufwand = Math.round(filtered.reduce((s, r) => s + r.aufwandRaw, 0));
  const goDetail = (key: string) => navigate(aPath(key, params.toString()));

  const setStufe = (v: string | null) => {
    const u = new URLSearchParams(params);
    if (!v || u.get("stufe") === v) u.delete("stufe"); else u.set("stufe", v);
    setParams(u, { replace: true });
  };
  const SChip = ({ v, label }: { v: string | null; label: string }) => (
    <button onClick={() => setStufe(v)}
      className={clsx("px-3 py-1 rounded-full text-[0.78rem] font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        (v === null ? !stufeF : stufeF === v) ? "bg-brand-500 border-brand-500 text-white" : "bg-surface border-border text-text-2 hover:border-brand-500")}>{label}</button>
  );

  const AutoRowView = ({ r }: { r: AutoRow }) => (
    <Trow cols={AUTO_COLS} onClick={() => goDetail(r.key)} label={r.name}>
      <span className="text-[0.84rem] font-medium text-text leading-snug line-clamp-2 pr-2" title={r.name}>{r.name}</span>
      <span className="text-[0.82rem] text-text-2">{r.dept ?? "—"}</span>
      <span>{r.hebel ? <LeverBadge lever={r.hebel} /> : <span className="text-[0.72rem] text-text-3">—</span>}</span>
      <span className="text-center">{r.stufe != null ? <StageBadge stufe={r.stufe} /> : <span className="text-[0.72rem] text-text-3">—</span>}</span>
      <ValueBar value={r.aufwand} max={maxHy} display={r.aufwand > 0 ? `~${fmtInt(r.aufwand)}` : "—"} dim={r.aufwand <= 0} className="w-full justify-self-end" />
      <span className="text-right text-[0.82rem] text-text-3 tabular-nums">{r.autom > 0 ? fmtInt(r.autom) : "—"}</span>
    </Trow>
  );

  return (
    <>
      <div className="mb-3 flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-x-2.5 gap-y-1.5 flex-wrap">
          <span className="text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-text-3">Stufe</span>
          <SChip v={null} label="Alle" />
          <SChip v="1" label="1 · Polling" />
          <SChip v="2" label="2 · Rezept" />
          <SChip v="3" label="3 · autonom" />
        </div>
        <div className="relative w-full lg:w-80">
          <Search className="w-4 h-4 text-text-3 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Automatisierung suchen …" aria-label="Automatisierung suchen"
            className="w-full rounded-control border border-border bg-surface pl-8 pr-3 py-2 text-sm text-text placeholder:text-text-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" />
        </div>
      </div>
      <div className="mb-3 flex items-baseline gap-x-2 gap-y-1 flex-wrap text-[0.8rem] text-text-3 px-0.5">
        <span className="ea-num font-semibold text-text tabular-nums">{loaded ? fmtInt(filtered.length) : "…"}</span>
        <span>von {loaded ? fmtInt(rows.length) : board.kpis.use_cases} Automatisierungen</span>
        <span>·</span>
        <span>übernommener Aufwand <span className="ea-num text-text font-semibold tabular-nums">≈ {fmtInt(totalAufwand)}</span> h/Jahr</span>
      </div>

      {!loaded && <div className="flex items-center gap-2 px-1 py-6 text-sm text-text-2"><Loader2 className="w-4 h-4 animate-spin" /> lädt Automatisierungen …</div>}

      {loaded && groups.map((grp) => (
        <div key={grp.st} className="mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <StatusPill status={grp.st === "bleibt_manuell" ? "offen" : grp.st} />
            <span className="text-[0.72rem] font-bold uppercase tracking-[0.05em] text-text-2">{AUTO_STATUS_LABEL[grp.st]}</span>
            <span className="text-[0.72rem] text-text-3">· {grp.rows.length} · ~{fmtInt(Math.round(grp.rows.reduce((s, r) => s + r.aufwandRaw, 0)))} h/J</span>
          </div>
          <DataTable>
            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <Thead cols={AUTO_COLS}>
                  <Th>Automatisierung</Th><Th>Abt.</Th><Th>Hebel</Th><Th align="center">Stufe</Th>
                  <Th align="right">Aufwand h/J</Th><Th align="right">autom. h/J</Th>
                </Thead>
                {grp.rows.map((r) => <AutoRowView key={r.key} r={r} />)}
              </div>
            </div>
          </DataTable>
        </div>
      ))}
      {loaded && groups.length === 0 && <div className="px-1 py-6 text-sm text-text-3">Keine Automatisierungen für diese Auswahl.</div>}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Automatisierungs-Detail (/a/:ucKey) — übernommene Aufgaben · Rezept/Fähigkeiten/
//   Stufe (Ex-Roadmap) · Digitalisierungs-Spezifikation · Entscheidungs-Block.
// ═════════════════════════════════════════════════════════════════════════════
function AutomationDetail({ board, ucKey, qs }: { board: BoardV3; ucKey: string; qs: string }) {
  const { list, loaded } = useUseCases();
  const { decisions } = useDecisions(true);
  const eff = useEffStatus();
  const [drawer, setDrawer] = useState<DrawerCard | null>(null);

  const uc = list.find((u) => u.use_case_key === ucKey);
  const attrs = board.automation_agent_attrs[ucKey];
  const taken = [...board.aufgaben.filter((a) => a.automation_uc_id === ucKey)]
    .sort((a, b) => b.handling_hours_per_year - a.handling_hours_per_year);
  const aufwand = Math.round(taken.reduce((s, a) => s + a.handling_hours_per_year, 0));
  const autom = Math.round(taken.reduce((s, a) => s + a.automatisierbar_hours_per_year, 0));
  const hebel = uc?.lead || taken[0]?.hebel || "";
  const isManual = hebel === "human_only";
  const status: AutoStatus = isManual ? "bleibt_manuell" : eff(ucKey);
  const mine = decisions.filter((d) => d.card_id === ucKey);
  // Digitalisierungs-Spez: Alt-Karten, deren automation_uc_ids diese Automatisierung enthalten.
  const digiCardIds = board.quick_wins
    .filter((w) => (w.automation_uc_ids || []).includes(ucKey))
    .map((w) => w.id);

  if (!loaded && !uc) return <div className="flex items-center gap-2 text-text-2 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> lädt …</div>;
  if (loaded && !uc) return <div className="p-2"><BackButton fallback={l1Path(buildFilterQs("automatisierungen", null, null))} label="Automatisierungen" /><EmptyState title="Automatisierung nicht gefunden" /></div>;

  const kpis = (
    <>
      <KpiChip label="Aufwand h/J" value={fmtInt(aufwand)} tone="brand" />
      <KpiChip label="autom. h/J" value={autom > 0 ? fmtInt(autom) : "—"} />
      <KpiChip label="Hebel" value={LEVER_LABEL[hebel] ?? hebel ?? "—"} />
      <KpiChip label="Aufgaben" value={taken.length} />
      {attrs?.stufe != null && <KpiChip label="Stufe" value={attrs.stufe} />}
      <KpiChip label="Status" value={AUTO_STATUS_LABEL[status]} tone={status === "live" || status === "wirkung_gemessen" ? "success" : status === "bleibt_manuell" ? "warning" : "brand"} />
    </>
  );

  const openDrawer = () => setDrawer({
    id: ucKey, kind: "automation", title: uc?.name || ucKey,
    hj: autom > 0 ? autom : null, hebelLabel: LEVER_LABEL[hebel] ?? hebel,
    effort: (uc?.effort || "").toUpperCase(), deepLink: aPath(ucKey, qs),
  });

  return (
    <EntityPage
      backTo={l1Path(buildFilterQs("automatisierungen", null, null))} backLabel="Automatisierungen"
      crumbs={[{ label: "Automatisierungen", to: l1Path(buildFilterQs("automatisierungen", null, null)) }, uc?.name || ucKey]}
      title={uc?.name || ucKey}
      subtitle={isManual ? "bleibt manuell — bewusste Nicht-Automatisierung (Rest-Aufwand bleibt ehrlich sichtbar)" : undefined}
      kpis={kpis}
      actions={
        <button onClick={openDrawer}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-control bg-action text-white text-[0.82rem] font-medium hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-surface">
          Entscheiden &amp; zuweisen
        </button>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-5 xl:items-start">
        <div className="min-w-0 space-y-4">
          <BlockCard title={`Übernommene Aufgaben (${taken.length})`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-text-3 text-left text-[0.68rem] uppercase tracking-wide">
                  <th className="py-1 pr-3 font-semibold">Aufgabe</th>
                  <th className="py-1 px-3 font-semibold">Abt.</th>
                  <th className="py-1 px-3 font-semibold text-right w-24">Aufwand h/J</th>
                  <th className="py-1 pl-3 font-semibold text-right w-20">autom. h/J</th>
                </tr></thead>
                <tbody>
                  {taken.map((a) => (
                    <tr key={a.id} className="border-t border-border align-top">
                      <td className="py-1.5 pr-3 text-text" title={a.name || undefined}>{a.name_de || a.name}</td>
                      <td className="py-1.5 px-3 text-text-2">{a.dept ?? "—"}</td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-text">{fmtInt(Math.round(a.handling_hours_per_year))}</td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-text-3">{a.automatisierbar_hours_per_year > 0 ? fmtInt(Math.round(a.automatisierbar_hours_per_year)) : "—"}</td>
                    </tr>
                  ))}
                  {taken.length === 0 && <tr><td colSpan={4} className="py-2 text-text-3">Keine Aufgaben zugeordnet.</td></tr>}
                </tbody>
              </table>
            </div>
          </BlockCard>

          {attrs && (attrs.recipe_kurz?.length ?? 0) > 0 && (
            <BlockCard title="Rezept — Auslöser → Endzustand (je Baustein)" accent>
              <ol className="space-y-2.5">
                {attrs.recipe_kurz.map((rk, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="w-6 h-6 rounded-full bg-brand-500 text-white text-[0.66rem] font-bold grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-[0.86rem] text-text leading-snug"><b className="font-medium">Auslöser:</b> {rk.trigger}</div>
                      {rk.end_states?.length > 0 && <div className="text-[0.8rem] text-text-2 leading-snug mt-0.5"><b className="font-medium">Endzustand:</b> {rk.end_states.join(" · ")}</div>}
                      <div className="text-[0.7rem] text-text-3 mt-0.5">{rk.n_steps} Schritte</div>
                    </div>
                  </li>
                ))}
              </ol>
            </BlockCard>
          )}

          {digiCardIds.length > 0 && <DigitalisierungBlock board={board} cardIds={digiCardIds} />}
        </div>

        <div className="space-y-4">
          {attrs && (
            <BlockCard title="Fähigkeiten & Stufe">
              <div className="flex items-center gap-2 mb-2.5"><StageBadge stufe={attrs.stufe} /><span className="text-[0.78rem] text-text-2">Agent-Reifegrad Stufe {attrs.stufe}</span></div>
              <div className="flex gap-1.5 flex-wrap">{attrs.capabilities.map((cap) => <CapDot key={cap} cap={cap} />)}</div>
            </BlockCard>
          )}

          {/* Entscheidungs-Block — Entscheidungen hängen an der Automatisierung (R4).
              Der EINE orange CTA sitzt oben (EntityPage actions); hier nur Status +
              Historie (Farbdisziplin: genau ein oranger CTA pro Ansicht). */}
          <BlockCard title="Entscheidung">
            <div className="flex items-center gap-2 mb-1">
              <StatusPill status={status === "bleibt_manuell" ? "offen" : status} />
              <span className="text-[0.78rem] text-text-2">{AUTO_STATUS_LABEL[status]}</span>
            </div>
            {mine.length === 0 && <p className="text-[0.75rem] text-text-3">Noch keine Entscheidung erfasst — „Entscheiden &amp; zuweisen" oben legt einen Atlas-Task mit Owner an.</p>}
            {mine.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {mine.map((d) => (
                  <div key={d.id} className="rounded-control border border-border px-2.5 py-1.5 text-[0.78rem]">
                    <b className="font-semibold text-text">{d.chosen_option || "Entschieden"}</b>
                    {d.owner_name && <span className="text-text-3"> · Owner {d.owner_name}</span>}
                    {d.rationale && <div className="text-[0.72rem] text-text-3 leading-snug mt-0.5">{d.rationale}</div>}
                  </div>
                ))}
              </div>
            )}
          </BlockCard>

          {uc && (
            <BlockCard title="Belege">
              <button onClick={() => window.open(ucPath(uc.id, ""), "_self")}
                className="inline-flex items-center gap-1.5 text-[0.82rem] font-medium text-brand-600 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">
                Beispiele &amp; Anhänge öffnen <ExternalLink className="w-3.5 h-3.5" />
              </button>
              <p className="mt-1.5 text-[0.72rem] text-text-3">Quote-first Beispiele aus den Original-Threads (Belege der übernommenen Aufgaben).</p>
            </BlockCard>
          )}
        </div>
      </div>

      <DecisionDrawer card={drawer} onClose={() => setDrawer(null)} />
    </EntityPage>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Prozessketten — L2 (Entity-Template: Soll-Schritte + Task-Tabelle + Querschnitt)
// ═════════════════════════════════════════════════════════════════════════════
function findFam(board: BoardV3, famId: string): { fam: ProzessFamilie; kette: Prozesskette } | null {
  for (const kette of board.prozessketten) {
    const fam = kette.familien.find((f) => f.family_id === famId);
    if (fam) return { fam, kette };
  }
  return null;
}
function ProzessL2({ board, famId, qs, dept, lever }: {
  board: BoardV3; famId: string; qs: string; dept: string | null; lever: string | null;
}) {
  const navigate = useNavigate();
  const eff = useEffStatus();
  const hit = findFam(board, famId);
  if (!hit) return <div className="p-2"><BackButton fallback={l1Path(qs)} label="Prozessketten" /><EmptyState title="Prozess nicht gefunden" /></div>;
  const { fam, kette } = hit;
  const steps = fam.soll_prozess || [];
  const byId = new Map(board.quick_wins.map((q) => [q.id, q] as const));
  const hy = dept ? Math.round(fam.dept_anteile?.hours_per_year?.[dept] ?? 0) : Math.round(fam.hours_per_year);
  const tasks = [...(fam.tasks || [])].sort((a, b) => b.volumen_6m - a.volumen_6m);
  const totalVol = tasks.reduce((s, t) => s + t.volumen_6m, 0);
  const human = fam.triage === "human_only";

  const kpis = (
    <>
      <KpiChip label="h/Jahr" value={fmtInt(hy)} tone="brand" />
      <KpiChip label="Vorgänge/6M" value={fmtInt(totalVol)} />
      <KpiChip label="Schritte" value={steps.length} />
      <KpiChip label="Tasks" value={tasks.length} />
      <KpiChip label="Triage" value={human ? "überwiegend human" : "automatisierbar"} tone={human ? "warning" : "success"} />
    </>
  );

  return (
    <EntityPage
      backTo={l1Path(qs)} backLabel="Prozessketten"
      crumbs={[{ label: kette.name, to: l1Path(qs) }, fam.label]}
      title={fam.label}
      subtitle={dept ? `Abteilungs-Sicht: ${dept}` : undefined}
      kpis={kpis}
    >
      {fam.dept_anteile && <div className="mb-4 max-w-md"><DeptShareBar da={fam.dept_anteile} /></div>}
      {fam.triage_begruendung && <p className="mb-4 text-sm text-text-2 leading-relaxed max-w-4xl">{fam.triage_begruendung}</p>}

      {steps.length > 0 && (
        <BlockCard title="Soll-Prozess — Schritte, Ampel & Lücke">
          <ol className="space-y-2">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="w-5 h-5 rounded-full bg-surface-3 text-text-2 text-[0.68rem] font-bold grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <AmpelDot a={s.ampel} />
                    <span className="text-[0.9rem] font-medium text-text">{s.schritt}</span>
                    {(s.doc_types || []).map((dt) => <span key={dt} className="inline-flex items-center px-1.5 rounded bg-surface-3 text-text-2 text-[0.64rem] font-medium">{docLabel(dt)}</span>)}
                    {s.akteur_von_an && <span className="text-[0.7rem] text-text-3">{akteurLabel(s.akteur_von_an)}</span>}
                  </div>
                  {s.luecke && <div className="text-[0.78rem] text-text-3 leading-snug mt-0.5">{s.luecke}</div>}
                </div>
              </li>
            ))}
          </ol>
        </BlockCard>
      )}

      <Section title="Tasks in diesem Prozess" right={<SortLabel>Volumen/6M absteigend</SortLabel>}>
        <Card padding="none" className="overflow-hidden">
          <div className="hidden md:grid grid-cols-[minmax(0,1.2fr)_84px_minmax(0,1.1fr)_minmax(0,1fr)_92px_92px_16px] gap-2.5 px-3.5 py-2 border-b border-border text-[0.68rem] uppercase tracking-wide text-text-3 font-semibold">
            <span>Task</span><span className="text-right">Vol./6M</span><span>Bestand</span><span>Automatisierung</span><span>Hebel</span><span>Status</span><span />
          </div>
          {tasks.map((t) => {
            const cardId = (t.zustaendige_karten || [])[0];
            const card = cardId ? byId.get(cardId) : undefined;
            const lev = card ? cardLever(card) : undefined;
            const status = cardId ? eff(cardId) : undefined;
            const dim = lever && lev !== lever;
            return (
              <div key={t.doc_typ} onClick={() => navigate(tPath(famId, t.doc_typ, qs))} {...keyActivate(() => navigate(tPath(famId, t.doc_typ, qs)))} aria-label={docLabel(t.doc_typ)}
                className={clsx("grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1.2fr)_84px_minmax(0,1.1fr)_minmax(0,1fr)_92px_92px_16px] gap-2.5 items-center px-3.5 py-2.5 cursor-pointer border-t border-border first:border-t-0 hover:bg-surface-2", dim && "opacity-45")}>
                <span className="text-sm font-medium text-text truncate" title={docLabel(t.doc_typ)}>{docLabel(t.doc_typ)}</span>
                <span className="hidden md:block text-right text-sm text-text-2 tabular-nums">{fmtInt(t.volumen_6m)}</span>
                <span className="hidden md:block text-[0.76rem] text-text-3 truncate" title={t.sf_bestand}>{sfBestandShort(t.sf_bestand)}</span>
                <span className="hidden md:block min-w-0">
                  {card
                    ? <span className="text-[0.78rem] text-text-2 truncate block" title={card.titel}>{card.titel}</span>
                    : <span className="text-[0.72rem] text-text-3">—</span>}
                </span>
                <span className="hidden md:block">{lev ? <LeverBadge lever={lev} /> : <span className="text-[0.72rem] text-text-3">—</span>}</span>
                <span className="hidden md:block">{status ? <StatusPill status={status} /> : <span className="text-[0.72rem] text-text-3">—</span>}</span>
                <ChevronRight className="hidden md:block w-4 h-4 text-text-3" />
              </div>
            );
          })}
          {tasks.length === 0 && <div className="px-3.5 py-4 text-sm text-text-3">Keine Tasks für diesen Prozess erfasst.</div>}
        </Card>
      </Section>

      <QuerschnittBlock board={board} chainId={kette.chain_id} dept={dept} qs={qs} />
    </EntityPage>
  );
}
function sfBestandShort(s?: string): string {
  if (!s) return "—";
  // Farbdisziplin v8: Status als Wort-Präfix statt Ampel-Glyph (kein ◐/○/●/✓).
  return s.replace(/^covered_manual\s*[—-]\s*/i, "abgedeckt · ").replace(/^partial\s*[—-]\s*/i, "teilweise · ").replace(/^covered_auto\s*[—-]\s*/i, "automatisiert · ").replace(/^none\s*[—-]\s*/i, "Lücke · ");
}

function QuerschnittBlock({ board, chainId, dept, qs }: { board: BoardV3; chainId: string; dept: string | null; qs: string }) {
  const navigate = useNavigate();
  const { list, loaded } = useUseCases();
  const chainDepts = Object.keys(board.crosswalk.dept_anteile.kette[chainId]?.hours_per_year || {});
  const items = list.filter((uc) => {
    const af = uc.artifact_family || "";
    const isAct = af.startsWith("act:") || af === "sonstige" || af === "" || !board.crosswalk.artifact_family_to_doc_typ[af];
    if (!isAct) return false;
    if (dept) return uc.dept === dept;
    return !uc.dept || chainDepts.includes(uc.dept);
  }).sort((a, b) => (b.hours_per_year ?? 0) - (a.hours_per_year ?? 0)).slice(0, 12);
  if (!loaded) return null;
  return (
    <Section title={`Querschnittsaufgaben${dept ? ` · ${dept}` : ""}`} right={<SortLabel>h/Jahr absteigend</SortLabel>}>
      {items.length === 0 ? (
        <div className="text-sm text-text-3">Keine Querschnittsaufgaben für diese Auswahl.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((uc) => (
            <button key={uc.id} onClick={() => navigate(ucPath(uc.id, qs))}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-surface hover:border-brand-500 text-[0.76rem] text-text-2">
              <span className="truncate max-w-[220px]">{uc.name || uc.id}</span>
              {uc.lead && <span className="text-[0.64rem] text-text-3">{LEVER_LABEL[uc.lead] ?? uc.lead}</span>}
              <span className="tabular-nums text-text-3">{fmtInt(uc.hours_per_year)} h/J</span>
            </button>
          ))}
        </div>
      )}
    </Section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Prozessketten — L3 (Entity-Template: Heute · Automatisierung · Digitalisierung)
// ═════════════════════════════════════════════════════════════════════════════
function TaskL3({ board, famId, docTyp, qs, dept }: {
  board: BoardV3; famId: string; docTyp: string; qs: string; dept: string | null;
}) {
  const navigate = useNavigate();
  const hit = findFam(board, famId);
  if (!hit) return <div className="p-2"><BackButton fallback={l1Path(qs)} label="Prozessketten" /><EmptyState title="Task nicht gefunden" /></div>;
  const { fam, kette } = hit;
  const task = (fam.tasks || []).find((t) => t.doc_typ === docTyp);
  const cardIds = task?.zustaendige_karten || [];
  const byId = new Map(board.quick_wins.map((q) => [q.id, q] as const));
  const primary = cardIds.map((id) => byId.get(id)).find(Boolean);
  const lev = primary ? cardLever(primary) : undefined;

  // SF-Durchgriff: erster Beleg-Thread aus den Digitalisierungs-Specs dieses Tasks.
  const sfRef = board.digitalization_specs
    .filter((s) => cardIds.includes(s.karte))
    .flatMap((s) => s.beispiel_attachment_refs || [])
    .find((r) => r.thread_id);
  const sfUrl = sfRef ? sfThreadUrl(sfRef.thread_id) : null;

  const scrollToDecision = () => document.getElementById("entscheidung")?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Feste KPI-Reihenfolge: Vorgänge/6M · Hebel · Aufwand · (Pfad als Zusatz).
  const kpis = task ? (
    <>
      <KpiChip label="Vorgänge/6M" value={fmtInt(task.volumen_6m)} tone="brand" />
      {lev && <KpiChip label="Hebel" value={LEVER_LABEL[lev] ?? lev} />}
      {primary?.effort_gesamt && <KpiChip label="Aufwand" value={primary.effort_gesamt.toUpperCase()} />}
      <KpiChip label="Pfad" value={pathLabel(task.verarbeitungspfad)} />
    </>
  ) : null;

  const actions = (
    <>
      {cardIds.length > 0 && (
        <button onClick={scrollToDecision}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-control bg-action text-white text-[0.82rem] font-medium hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-2 focus-visible:ring-offset-surface">
          Entscheiden &amp; zuweisen
        </button>
      )}
      {sfUrl && (
        <a href={sfUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border text-[0.82rem] font-medium text-text-2 hover:border-brand-500">
          SF <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </>
  );

  const heute = (
    <BlockCard title="Heute — Bestand">
      {task ? (
        <div className="space-y-2 text-sm">
          <div className="text-text-2"><b className="text-text font-medium">Verarbeitungspfad:</b> {pathLabel(task.verarbeitungspfad)}</div>
          {task.verarbeitungspfad_begruendung && <div className="text-[0.8rem] text-text-3 leading-snug">{task.verarbeitungspfad_begruendung}</div>}
          {task.sf_bestand && <BestandSplit text={task.sf_bestand} />}
        </div>
      ) : <div className="text-sm text-text-3">Kein Bestand hinterlegt.</div>}
    </BlockCard>
  );

  const automation = (
    <div id="entscheidung" className="space-y-4 scroll-mt-4 xl:sticky xl:top-2">
      {cardIds.length === 0 && <BlockCard title="Automatisierung"><div className="text-sm text-text-3">Keine Automatisierung zugeordnet.</div></BlockCard>}
      {cardIds.map((cid) => {
        const card = byId.get(cid);
        if (!card) return null;
        const roi = task?.karten_roi?.[cid];
        const cl = cardLever(card);
        return (
          <BlockCard key={cid} title="Automatisierung" accent>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-sm font-semibold text-text">{card.titel}</span>
              <LeverBadge lever={cl} />
              <EffortPill effort={card.effort_gesamt} />
              {card.materialized_from && <span className="text-[0.62rem] px-1.5 py-0.5 rounded-full bg-surface-3 text-text-3">neu materialisiert</span>}
            </div>
            {roi && roi.hours_per_year != null && <div className="text-[0.8rem] text-text-2 mb-1">ROI: <b className="text-text tabular-nums">{fmtInt(roi.hours_per_year)} h/Jahr</b> <span className="text-text-3">({roi.roi_quelle})</span></div>}
            {card.prerequisites?.length ? (
              <div className="text-[0.8rem] text-text-2 mb-1">Voraussetzungen: {card.prerequisites.map((pr) => <PrChipInline key={pr} id={pr} qs={qs} />)}</div>
            ) : null}
            {card.risiko && <div className="text-[0.78rem] text-text-3 mb-1"><b className="text-text-2">Risiko:</b> {card.risiko}</div>}
            <TechDetails><div className="text-[0.72rem] font-mono text-text-3">Referenz: {cid}</div></TechDetails>
            <DecisionBlock card={{ id: card.id, kind: "quick_win", title: card.titel }} questions={card.entscheidungen_offen} primaryCta={false} />
          </BlockCard>
        );
      })}
    </div>
  );

  return (
    <EntityPage
      backTo={pPath(famId, qs)} backLabel={fam.label}
      crumbs={[{ label: kette.name, to: l1Path(qs) }, { label: fam.label, to: pPath(famId, qs) }, docLabel(docTyp)]}
      title={docLabel(docTyp)}
      subtitle={task ? `${fmtInt(task.volumen_6m)} Vorgänge/6M · Quelle ${task.volumen_quelle}` : undefined}
      kpis={kpis}
      actions={actions}
    >
      {/* Sektions-Sprungleiste: Kernfrage (Heute/Automatisierung/Entscheidung) above
          fold, Digitalisierung + Belege ≤1 Klick darunter (AK 4). */}
      <JumpBar items={[
        { id: "heute", label: "Heute" },
        { id: "entscheidung", label: "Automatisierung & Entscheidung" },
        { id: "digitalisierung", label: "Digitalisierung" },
        { id: "belege", label: "Belege" },
      ]} />
      {/* Analyse (links, hoch) | Entscheidung (rechts, sticky) — beide substanziell,
          keine halbleere Spalte. <xl: gestapelt (Entscheidung folgt der Analyse). */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5 xl:items-start">
        <div className="min-w-0 space-y-5">
          <div id="heute" className="scroll-mt-4">{heute}</div>
          <div id="digitalisierung" className="scroll-mt-4"><DigitalisierungBlock board={board} cardIds={cardIds} /></div>
          <div id="belege" className="scroll-mt-4"><BelegeBlock board={board} docTyp={docTyp} dept={dept} qs={qs} /></div>
        </div>
        {automation}
      </div>

      {/* Sekundaerer Absprung, damit man ohne Browser-Back zurueck in die Kette kommt */}
      <div className="mt-6 text-[0.78rem] text-text-3">
        <button onClick={() => navigate(pPath(famId, qs))} className="inline-flex items-center gap-1 hover:text-text">
          <ArrowLeft className="w-3.5 h-3.5" /> zurück zu {fam.label}
        </button>
      </div>
    </EntityPage>
  );
}

function BestandSplit({ text }: { text: string }) {
  const clean = text.replace(/^(covered_manual|covered_auto|partial|none)\s*[—-]\s*/i, "");
  const zustand = /^covered/i.test(text) ? "vorhanden" : /^partial/i.test(text) ? "teilweise" : "Lücke";
  return (
    <div className="rounded-md bg-surface-2 p-2.5 text-[0.82rem] text-text-2 leading-relaxed">
      <span className={clsx("inline-flex items-center px-1.5 rounded text-[0.66rem] font-bold mr-1.5", zustand === "vorhanden" ? "bg-success/10 text-success" : zustand === "teilweise" ? "bg-warning/10 text-warning" : "bg-danger/10 text-danger")}>{zustand}</span>
      {clean}
    </div>
  );
}
function PrChipInline({ id, qs }: { id: string; qs: string }) {
  const navigate = useNavigate();
  return (
    <button onClick={(e) => { e.stopPropagation(); navigate(decidePath(id, qs)); }}
      className="inline-flex items-center px-1.5 py-0.5 mr-1 rounded bg-danger/10 text-danger text-[0.68rem] font-semibold hover:bg-danger hover:text-white transition-colors">{id}</button>
  );
}

// ── Aufklappbare Technik-Details (verlagert Insider-Jargon aus L1/L2 herunter) ─
function TechDetails({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="my-2">
      <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-[0.72rem] text-text-3 hover:text-text">
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Technik-Details
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  );
}

// ── Digitalisierung: gruppierte Feld-Tabelle (Feld | Objekt | Zweck-Gruppe) ───
const PROV_LABEL: Record<string, [string, string]> = {
  kuratiert: ["kuratiert", "bg-success/10 text-success"],
  v1_llm: ["v1-Entwurf — zu verifizieren", "bg-warning/10 text-warning"],
  keine_19er_bruecke: ["Daten-Gap — keine UC-Brücke", "bg-danger/10 text-danger"],
  gemessen_email_examples: ["gemessen (Email-Belege)", "bg-success/10 text-success"],
  stages_kuratiert: ["kuratiert", "bg-success/10 text-success"],
};
function ProvBadge({ prov }: { prov?: string }) {
  if (!prov) return null;
  const [label, cls] = PROV_LABEL[prov] ?? [prov, "bg-surface-3 text-text-2"];
  return <span className={clsx("inline-flex items-center px-1.5 py-0.5 rounded text-[0.62rem] font-semibold whitespace-nowrap", cls)}>{label}</span>;
}

type Gruppe = "outbound" | "inbound" | "gap";
const GRUPPE_META: Record<Gruppe, { label: string; cls: string }> = {
  outbound: { label: "Anlegen/Senden", cls: "bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300" },
  inbound: { label: "Extrahieren", cls: "bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300" },
  gap: { label: "Anlage-Lücke", cls: "bg-warning/10 text-warning" },
};
function fieldRows(f: Felder): { feld: string; objekt: string; gruppe: Gruppe }[] {
  const rows: { feld: string; objekt: string; gruppe: Gruppe }[] = [];
  const push = (arr: string[], gruppe: Gruppe) => (arr || []).forEach((s) => {
    const dot = s.includes(".");
    rows.push({ feld: dot ? s.split(".").slice(1).join(".") : s, objekt: dot ? s.split(".")[0] : "—", gruppe });
  });
  push(f.outbound_data_fields, "outbound");
  push(f.inbound_extract_fields, "inbound");
  push(f.enrichment_gaps, "gap");
  return rows;
}

function DigitalisierungBlock({ board, cardIds }: { board: BoardV3; cardIds: string[] }) {
  const specs = board.digitalization_specs.filter((s) => cardIds.includes(s.karte));
  return (
    <BlockCard title="Digitalisierung — welche Felder anlegen / extrahieren">
      {specs.length === 0 ? (
        <div className="text-sm text-text-3">Keine Digitalisierungs-Spezifikation hinterlegt (Daten-Gap).</div>
      ) : (
        <div className="space-y-4">
          {specs.map((s) => {
            const f = s.felder || { outbound_data_fields: [], inbound_extract_fields: [], inbound_checks: [], enrichment_gaps: [] };
            const rows = fieldRows(f);
            return (
              <div key={s.karte} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 flex-wrap mb-2.5">
                  <span className="text-[0.86rem] font-semibold text-text">{s.titel}</span>
                  <ProvBadge prov={s.provenienz?.felder} />
                </div>

                {rows.length === 0 && s.note ? (
                  <div className="rounded-md bg-danger/5 border border-danger/20 px-2.5 py-2 text-[0.78rem] text-text-2 leading-snug mb-3">
                    <b className="text-danger">Daten-Gap:</b> {s.note}
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-[0.8rem] text-text-3 mb-3">— keine Felder hinterlegt</div>
                ) : (
                  <div className="overflow-x-auto mb-3">
                    <table className="w-full text-[0.8rem]">
                      <thead>
                        <tr className="text-left text-[0.66rem] uppercase tracking-wide text-text-3">
                          <th className="py-1 pr-3 font-semibold">Feld</th>
                          <th className="py-1 px-3 font-semibold">Objekt</th>
                          <th className="py-1 pl-3 font-semibold">Zweck-Gruppe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} className="border-t border-border align-top">
                            <td className="py-1.5 pr-3 font-mono text-text-2">{r.feld}</td>
                            <td className="py-1.5 px-3 text-text-3">{r.objekt === "—" ? "—" : <span className="font-mono">{r.objekt}</span>}</td>
                            <td className="py-1.5 pl-3">
                              <span className={clsx("inline-flex items-center px-1.5 py-0.5 rounded text-[0.66rem] font-semibold whitespace-nowrap", GRUPPE_META[r.gruppe].cls)}>{GRUPPE_META[r.gruppe].label}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {f.inbound_checks && f.inbound_checks.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[0.66rem] font-semibold uppercase tracking-wide text-text-3 mb-1">Prüfregeln (inbound checks)</div>
                    <ul className="space-y-1 text-[0.78rem] text-text-2 list-disc pl-5">
                      {f.inbound_checks.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}

                <div className="mb-2">
                  <span className="text-[0.66rem] font-semibold uppercase tracking-wide text-text-3 mr-2">Benötigte Dokumente</span>
                  {s.doc_types.map((dt) => <span key={dt} className="inline-flex items-center px-1.5 py-0.5 mr-1 rounded bg-surface-3 text-text-2 text-[0.72rem] font-medium">{docLabel(dt)}</span>)}
                </div>
                {s.beispiel_attachment_refs && s.beispiel_attachment_refs.length > 0 && (
                  <BeispielDokumente refs={s.beispiel_attachment_refs} prov={s.provenienz?.beispiel_attachment_refs} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </BlockCard>
  );
}

// Beispiel-Dokumente: SF-Thread-Deeplink + Betreff + Inline-Byte-Vorschau.
function BeispielDokumente({ refs, prov }: { refs: AttRef[]; prov?: string }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? refs : refs.slice(0, 4);
  return (
    <div className="mt-1">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[0.66rem] font-semibold uppercase tracking-wide text-text-3">Beispiel-Dokumente aus den Emails</span>
        <ProvBadge prov={prov} />
        <span className="text-[0.66rem] text-text-3">{refs.length}</span>
      </div>
      <div className="space-y-2">
        {shown.map((r, i) => <BeispielDokRow key={i} r={r} />)}
      </div>
      {refs.length > 4 && (
        <button onClick={() => setExpanded((v) => !v)} className="mt-1.5 text-[0.74rem] text-text-2 hover:text-text">
          {expanded ? "weniger" : `alle ${refs.length} zeigen`}
        </button>
      )}
    </div>
  );
}
function BeispielDokRow({ r }: { r: AttRef }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [atts, setAtts] = useState<Attachment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sfUrl = sfThreadUrl(r.thread_id);

  async function loadPreview() {
    if (atts !== null) { setOpen((v) => !v); return; }
    setLoading(true); setErr(null);
    try {
      const detail = await api.get<UseCaseDetail>(`/email-automation/${r.uc_id}`);
      const ex: Example | undefined = detail.examples?.[r.example_index] ?? detail.examples?.find((e) => (e.attachments || []).length > 0);
      setAtts(ex?.attachments ?? []);
      setOpen(true);
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-surface-2">
        <FileText className="w-4 h-4 text-text-3 shrink-0" />
        <span className="min-w-0 flex-1 text-[0.76rem] text-text-2 truncate" title={r.subject}>{r.subject || r.internet_message_id || "(Beleg-Thread)"}</span>
        {r.mailbox && <span className="hidden sm:block text-[0.66rem] text-text-3 truncate max-w-[160px]">{r.mailbox}</span>}
        {sfUrl && <a href={sfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[0.68rem] font-semibold text-brand-600 hover:text-brand-700 shrink-0" title="Salesforce-Thread öffnen">SF ↗</a>}
        <button onClick={loadPreview} className="inline-flex items-center gap-0.5 text-[0.68rem] text-text-3 hover:text-text shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
          {open ? "verbergen" : "Anhang"}
        </button>
      </div>
      {open && (
        <div className="p-2 space-y-2 bg-surface">
          {err && <div className="text-[0.74rem] text-danger">Vorschau nicht verfügbar: {err}</div>}
          {atts && atts.length === 0 && <div className="text-[0.74rem] text-text-3">Kein extrahierter Anhang zu diesem Beleg — Thread über „SF ↗" öffnen.</div>}
          {atts && atts.map((a) => <AttachmentPreview key={a.id} att={a} onError={(m) => toast.err("Fehler", m)} />)}
        </div>
      )}
    </div>
  );
}

// ── Aufgaben & Belege: UCs zum doc_typ (via Crosswalk artifact_family→doc_typ) ─
function BelegeBlock({ board, docTyp, dept, qs }: { board: BoardV3; docTyp: string; dept: string | null; qs: string }) {
  const navigate = useNavigate();
  const { list, loaded } = useUseCases();
  const afForDoc = Object.entries(board.crosswalk.artifact_family_to_doc_typ)
    .filter(([, v]) => v.doc_typ === docTyp).map(([af]) => af);
  const ucs = list.filter((uc) => uc.artifact_family && afForDoc.includes(uc.artifact_family) && (!dept || uc.dept === dept))
    .sort((a, b) => (b.hours_per_year ?? 0) - (a.hours_per_year ?? 0));

  return (
    <BlockCard title="Aufgaben & Belege — echte Fälle aus der 6-Monats-Analyse">
      {!loaded ? (
        <div className="flex items-center gap-2 text-sm text-text-3"><Loader2 className="w-4 h-4 animate-spin" /> Katalog lädt …</div>
      ) : ucs.length === 0 ? (
        <div className="text-sm text-text-3">
          Keine UC-Brücke für <span className="font-medium">{docLabel(docTyp)}</span>{dept ? ` (${dept})` : ""} — dieser Doc-Typ hat keine artifact_family-UC. Belege später über einen eigenen Doc-Typ-UC.
        </div>
      ) : (
        <div className="space-y-2">
          <SortLabel>h/Jahr absteigend</SortLabel>
          {ucs.map((uc) => <UcBelegRow key={uc.id} uc={uc} onOpen={() => navigate(ucPath(uc.id, qs))} />)}
        </div>
      )}
    </BlockCard>
  );
}
function UcBelegRow({ uc, onOpen }: { uc: UseCaseOverview; onOpen: () => void }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<UseCaseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  async function toggle() {
    if (!open && detail === null) {
      setLoading(true);
      try { setDetail(await api.get<UseCaseDetail>(`/email-automation/${uc.id}`)); }
      catch (e) { toast.err("Konnte Belege nicht laden", (e as Error).message); }
      finally { setLoading(false); }
    }
    setOpen((v) => !v);
  }
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-surface-2">
        <button onClick={toggle} className="min-w-0 flex-1 text-left flex items-center gap-2">
          <span className="text-sm font-medium text-text truncate">{uc.name || uc.id}</span>
          {uc.quick_win && <Star className="w-3.5 h-3.5 text-warning fill-warning shrink-0" />}
        </button>
        {uc.dept && <span className="text-[0.68rem] px-1.5 py-0.5 rounded bg-surface-3 text-text-2 shrink-0">{uc.dept}</span>}
        {uc.lead && <span className="hidden sm:inline"><LeverBadge lever={uc.lead} /></span>}
        <span className="text-[0.76rem] text-text-2 tabular-nums shrink-0">{fmtInt(uc.hours_per_year)} h/J</span>
        <button onClick={onOpen} className="inline-flex items-center gap-0.5 text-[0.7rem] font-semibold text-brand-600 hover:text-brand-700 shrink-0" title="Volle Aufgaben-Detailseite">Detail <ArrowRight className="w-3 h-3" /></button>
      </div>
      {open && (
        <div className="p-3 bg-surface">
          {loading && <div className="flex items-center gap-2 text-sm text-text-3"><Loader2 className="w-4 h-4 animate-spin" /> lädt …</div>}
          {detail && detail.examples.length === 0 && <div className="text-sm text-text-3">Keine Beispiel-Fälle hinterlegt.</div>}
          {detail && detail.examples.length > 0 && (
            <div className="space-y-3">
              {detail.examples.slice(0, 5).map((e) => <ExampleCard key={e.id} e={e} onError={(m) => toast.err("Fehler", m)} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function cardHours(c: QuickWinV3): number {
  return Math.round(c.hours_per_year ?? hoursYear((c.volumen?.saved_hours_6mo as number | undefined) ?? c.saved_hours_6mo));
}

// ═════════════════════════════════════════════════════════════════════════════
// Entscheidungen — L1 (Offen + Getroffen; L2 = /decide/:cardId via DecisionBlock)
// ═════════════════════════════════════════════════════════════════════════════
const PREREQ_COLS = "30px 58px minmax(0,1fr) 210px 44px";
const DEC_COLS = "minmax(0,1fr) 150px 118px 44px";

function EntscheidungenL1({ board, dept, lever, qs }: { board: BoardV3; dept: string | null; lever: string | null; qs: string }) {
  const navigate = useNavigate();
  const eff = useEffStatus();
  const { decisions } = useDecisions(true);
  const [drawer, setDrawer] = useState<DrawerCard | null>(null);
  const goDecide = (id: string) => navigate(decidePath(id, qs));

  const prs = board.prerequisites
    .filter((p) => p.blocking === "hard" && p.status !== "done")
    .sort((a, b) => (b.unlock_hours_per_year ?? 0) - (a.unlock_hours_per_year ?? 0));
  const maxUnlock = Math.max(1, ...prs.map((p) => p.unlock_hours_per_year ?? 0));

  let cards = board.quick_wins.filter((q) => eff(q.id) === "offen");
  if (lever) cards = cards.filter((q) => cardLever(q) === lever);
  cards = cards.sort((a, b) => cardHours(b) - cardHours(a));
  const maxCardHy = Math.max(1, ...cards.map(cardHours));

  const getroffen = decisions.filter((d) => !dept || d.owner_department === dept);

  const openDrawer = (c: QuickWinV3) => setDrawer({
    id: c.id, kind: "quick_win", title: c.titel,
    hj: cardHours(c) > 0 ? cardHours(c) : null,
    hebelLabel: LEVER_LABEL[cardLever(c)] ?? cardLever(c),
    effort: (c.effort_gesamt || "").toUpperCase(),
    deepLink: decidePath(c.id, qs),
  });

  return (
    <>
      {/* Voraussetzungen — Rang-Liste (schalten Stunden frei) */}
      <SectionHeader>Voraussetzungen — schalten Stunden frei</SectionHeader>
      <DataTable className="mb-6">
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <Thead cols={PREREQ_COLS}>
              <Th align="center">#</Th><Th>ID</Th><Th>Voraussetzung</Th><Th>Schaltet frei</Th><Th align="center">Aufw.</Th>
            </Thead>
            {prs.map((pr, i) => (
              <Trow key={pr.id} cols={PREREQ_COLS} onClick={() => goDecide(pr.id)} label={pr.titel}>
                <span className="flex justify-center">
                  <span className={clsx(
                    "w-6 h-6 rounded-control grid place-items-center font-numeric text-[0.78rem] font-bold",
                    i === 0 ? "bg-accent-500 text-white" : "bg-accent-50 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300",
                  )}>{i + 1}</span>
                </span>
                <span className="text-[0.75rem] font-bold text-brand-500 tracking-wide">{pr.id}</span>
                <span className="text-[0.84rem] font-medium text-text truncate pr-2">{pr.titel}</span>
                <span className="flex flex-col gap-1">
                  <span className="text-[0.74rem] text-text-2 tabular-nums"><b className="text-accent-700 dark:text-accent-300">~{fmtInt(pr.unlock_hours_per_year)} h/J</b> frei</span>
                  <HoursBar value={pr.unlock_hours_per_year ?? 0} max={maxUnlock} />
                </span>
                <span className="flex justify-center"><Chip variant="effort">{(pr.effort || "—").toUpperCase()}</Chip></span>
              </Trow>
            ))}
            {prs.length === 0 && <div className="px-[18px] py-4 text-sm text-text-3">Keine offenen Voraussetzungen.</div>}
          </div>
        </div>
      </DataTable>

      {/* Offene Entscheidungen — ruhige Liste, ganze Zeile → Drawer */}
      <SectionHeader>Offene Entscheidungen — sortiert nach freigeschalteten h/Jahr</SectionHeader>
      <DataTable>
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            <Thead cols={DEC_COLS}>
              <Th>Entscheidung</Th><Th>Hebel</Th><Th align="right">~h/Jahr</Th><Th align="center">Aufw.</Th>
            </Thead>
            {cards.map((c) => (
              <Trow key={c.id} cols={DEC_COLS} onClick={() => openDrawer(c)} label={c.titel}>
                <span className="text-[0.84rem] font-medium text-text leading-snug line-clamp-2 pr-2">{c.titel}</span>
                <span><LeverBadge lever={cardLever(c)} /></span>
                <ValueBar value={cardHours(c)} max={maxCardHy} display={cardHours(c) > 0 ? `~${fmtInt(cardHours(c))}` : "—"} dim={cardHours(c) <= 0} className="justify-self-end w-full" />
                <span className="flex justify-center"><Chip variant="effort">{(c.effort_gesamt || "—").toUpperCase()}</Chip></span>
              </Trow>
            ))}
            {cards.length === 0 && <div className="px-[18px] py-4 text-sm text-text-3">Keine offenen Entscheidungen{lever ? ` für ${LEVER_LABEL[lever]}` : ""}.</div>}
          </div>
        </div>
      </DataTable>

      {/* Getroffen */}
      <div className="mt-6"><SectionHeader>{`Getroffen (${getroffen.length})`}</SectionHeader></div>
      {getroffen.length === 0 ? (
        <div className="text-sm text-text-3">Noch keine Entscheidung erfasst. „Entscheiden & zuweisen" legt einen echten Atlas-Task mit Owner an.</div>
      ) : (
        <DataTable>
          {getroffen.map((d) => (
            <div key={d.id} onClick={() => goDecide(d.card_id)} {...keyActivate(() => goDecide(d.card_id))} aria-label={d.card_title || d.card_id}
              className="flex items-center gap-2 px-[18px] py-2.5 cursor-pointer border-t border-border first:border-t-0 hover:bg-accent-500/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-500">
              <span className="text-[0.72rem] text-text-3 tabular-nums shrink-0">{new Date(d.created_at).toLocaleDateString("de-DE")}</span>
              <span className="text-sm text-text truncate flex-1 min-w-0"><b className="font-semibold">{d.chosen_option || "Entschieden"}</b> · {d.card_title || d.card_id}</span>
              {d.owner_name && <span className="hidden sm:block text-[0.72rem] text-text-3 shrink-0">Owner {d.owner_name}{d.owner_department ? ` (${d.owner_department})` : ""}</span>}
              <StatusPill status={(d.status as CardStatus) || "entschieden"} />
              {d.task_id && <span className="text-[0.7rem] font-semibold text-brand-600 shrink-0">Task ↗</span>}
            </div>
          ))}
        </DataTable>
      )}

      <DecisionDrawer card={drawer} onClose={() => setDrawer(null)} />
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Agent-Roadmap — L1 (3-Spalten-Board HEUTE / NÄCHSTER SCHRITT / ZIELBILD + Gates)
// ═════════════════════════════════════════════════════════════════════════════
const STAGE_COL: (null | { badge: string; color: string })[] = [
  null,
  { badge: "HEUTE LIVE", color: "text-success" },
  { badge: "NÄCHSTER SCHRITT", color: "text-accent-600 dark:text-accent-300" },
  { badge: "ZIELBILD", color: "text-text-3" },
];

function StageBadge({ stufe }: { stufe: number }) {
  const c = STAGE_COL[stufe];
  if (!c) return null;
  return <div className={clsx("text-[0.66rem] font-bold tracking-wide mb-1", c.color)}>{c.badge}</div>;
}

function CapDot({ cap }: { cap: string }) {
  const blocked = capBlocked(cap);
  return (
    <span className={clsx("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[0.6rem] font-medium whitespace-nowrap",
      blocked ? "bg-warning/10 text-warning" : "bg-success/10 text-success")}
      title={blocked ? "geblockt — braucht Voraussetzung (PR)" : "verfügbar"}>
      <span className={clsx("w-1.5 h-1.5 rounded-full", blocked ? "bg-warning" : "bg-success")} />
      {capLabel(cap)}
    </span>
  );
}
